import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search, 
  UserCog,
  Shield,
  Mail,
  Phone,
  Key,
  X,
  Check,
  Ban,
  Settings,
  ChevronDown,
  ChevronRight,
  UserCheck,
  Link2,
  Link2Off
} from 'lucide-react';

// Permission Labels in French
const PERMISSION_LABELS = {
  reservations: 'Réservations',
  guests: 'Clients',
  sales: 'Ventes',
  products: 'Produits',
  expenses: 'Dépenses',
  charges: 'Charges',
  suppliers: 'Fournisseurs',
  cash: 'Caisse',
  hr: 'RH',
  reports: 'Rapports',
  settings: 'Paramètres',
  users: 'Utilisateurs',
  societe: 'Données société',
  finance: 'Finance',
  dashboard: 'Tableau de bord'
};

const UsersPage = () => {
  const { can } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role_id: '',
    employee_id: '',
    active: true
  });
  const [newPassword, setNewPassword] = useState('');
  const [roleForm, setRoleForm] = useState({
    name: '',
    code: '',
    description: '',
    permissions: []
  });
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchEmployees();
    fetchAvailablePermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.employees || response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await api.get('/roles/permissions');
      setAvailablePermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, formData);
        toast.success('Utilisateur modifié');
      } else {
        await api.post('/users', formData);
        toast.success('Utilisateur créé');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${selectedUserId}/reset-password`, { new_password: newPassword });
      toast.success('Mot de passe réinitialisé');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUserId(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { active: !user.active });
      toast.success(user.active ? 'Utilisateur désactivé' : 'Utilisateur activé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      role_id: '',
      employee_id: '',
      active: true
    });
    setEditingUser(null);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || '',
      role_id: user.role_id,
      employee_id: user.employee_id || '',
      active: user.active
    });
    setShowModal(true);
  };

  const openPasswordModal = (userId) => {
    setSelectedUserId(userId);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const openPermissionsModal = (role) => {
    setEditingRole(role);
    setEditedPermissions([...role.permissions]);
    // Expand all modules by default
    const expanded = {};
    Object.keys(availablePermissions).forEach(m => { expanded[m] = true; });
    setExpandedModules(expanded);
    setShowPermissionsModal(true);
  };

  const togglePermission = (permValue) => {
    if (editedPermissions.includes(permValue)) {
      setEditedPermissions(editedPermissions.filter(p => p !== permValue));
    } else {
      setEditedPermissions([...editedPermissions, permValue]);
    }
  };

  const toggleModule = (module) => {
    setExpandedModules({ ...expandedModules, [module]: !expandedModules[module] });
  };

  const savePermissions = async () => {
    try {
      await api.put(`/roles/${editingRole.id}/permissions`, { permissions: editedPermissions });
      toast.success('Permissions mises à jour');
      setShowPermissionsModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Role CRUD functions
  const resetRoleForm = () => {
    setRoleForm({ name: '', code: '', description: '', permissions: [] });
    setEditingRole(null);
  };

  const openAddRoleModal = () => {
    resetRoleForm();
    setShowRoleModal(true);
  };

  const openEditRoleModal = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      code: role.code,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowRoleModal(true);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    if (!roleForm.name || !roleForm.code) {
      toast.error('Nom et code requis');
      return;
    }
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, roleForm);
        toast.success('Rôle mis à jour');
      } else {
        await api.post('/roles', roleForm);
        toast.success('Rôle créé');
      }
      setShowRoleModal(false);
      resetRoleForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Supprimer ce rôle ?')) return;
    try {
      await api.delete(`/roles/${roleId}`);
      toast.success('Rôle supprimé');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const toggleRolePermission = (permCode) => {
    const current = roleForm.permissions || [];
    if (current.includes(permCode)) {
      setRoleForm({ ...roleForm, permissions: current.filter(p => p !== permCode) });
    } else {
      setRoleForm({ ...roleForm, permissions: [...current, permCode] });
    }
  };

  const filteredUsers = users.filter(user =>
    `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Propriétaire': return 'bg-purple-100 text-purple-800';
      case 'Gouvernante': return 'bg-blue-100 text-blue-800';
      case 'Femme de ménage': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} utilisateurs</p>
        </div>
        {can('users.create') && (
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4" />
            Nouvel utilisateur
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'users' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <UserCog className="w-4 h-4 inline mr-2" />
          Utilisateurs
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'roles' 
              ? 'border-primary text-primary' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Rôles & Permissions
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un utilisateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="search-users"
            />
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.phone && (
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {user.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role?.name)}`}>
                      <Shield className="w-3 h-3" />
                      {user.role?.name || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.employee ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title={`${user.employee.first_name} ${user.employee.last_name} - ${user.employee.position || 'N/A'}`}>
                        <Link2 className="w-3 h-3" />
                        Lié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <Link2Off className="w-3 h-3" />
                        Non lié
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => can('users.edit') && handleToggleActive(user)}
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      } ${can('users.edit') ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                      data-testid={`toggle-active-${user.id}`}
                    >
                      {user.active ? <Check className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                      {user.active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {can('users.edit') && (
                        <>
                          <button
                            onClick={() => openPasswordModal(user.id)}
                            className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Réinitialiser mot de passe"
                            data-testid={`reset-pwd-${user.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            title="Modifier"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {can('users.delete') && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="user-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingUser ? 'Modifier utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="input-field"
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="input-field"
                    required
                    data-testid="input-last-name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                  required
                  disabled={!!editingUser}
                  data-testid="input-email"
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-field"
                    required={!editingUser}
                    minLength={6}
                    data-testid="input-password"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-field"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
                <select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  className="input-field"
                  required
                  data-testid="select-role"
                >
                  <option value="">Sélectionner un rôle</option>
                  {roles
                    .filter(role => !role.is_system && !['owner', 'super_admin'].includes(role.code))
                    .map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Liaison avec profil employé */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4" />
                    Profil employé (optionnel)
                  </span>
                </label>
                <select
                  value={formData.employee_id || ''}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value || null })}
                  className="input-field"
                  data-testid="select-employee"
                >
                  <option value="">Aucun profil employé</option>
                  {employees
                    .filter(emp => emp.is_active !== false)
                    .map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.position || 'N/A'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Lier ce compte à un profil employé permet d'accéder au pointage et aux heures de travail.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" data-testid="submit-user">
                  {editingUser ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm" data-testid="password-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Réinitialiser mot de passe</h2>
              <button onClick={() => setShowPasswordModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleResetPassword} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field"
                  required
                  minLength={6}
                  data-testid="input-new-password"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" data-testid="submit-password">
                  Réinitialiser
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold">Rôles et permissions</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Configurez les droits d'accès pour chaque rôle de votre établissement
              </p>
            </div>
            {can('users.create') && (
              <button
                onClick={openAddRoleModal}
                className="btn-primary flex items-center gap-2"
                data-testid="add-role-btn"
              >
                <Plus className="w-4 h-4" />
                Nouveau rôle
              </button>
            )}
          </div>
          
          {/* Roles Legend */}
          <div className="p-3 bg-gray-50 border-b flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Rôle système (non modifiable)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Rôle personnalisé
            </span>
          </div>
          
          <div className="divide-y">
            {roles.map((role) => {
              const permCount = typeof role.permissions === 'string' 
                ? JSON.parse(role.permissions || '[]').length 
                : (role.permissions?.length || 0);
              const isSystemRole = role.is_system || role.code === 'owner' || role.code === 'super_admin';
              const hasAllAccess = permCount === 0 && isSystemRole;
              
              return (
                <div
                  key={role.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${!role.active ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isSystemRole ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                      }`}>
                        <Shield className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{role.name}</p>
                          {isSystemRole && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              Système
                            </span>
                          )}
                          {!role.active && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              Inactif
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {hasAllAccess 
                            ? 'Accès complet à toutes les fonctionnalités' 
                            : role.description || `${permCount} permission${permCount > 1 ? 's' : ''} configurée${permCount > 1 ? 's' : ''}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {can('users.edit') && (
                        <button
                          onClick={() => openPermissionsModal(role)}
                          className="btn-secondary flex items-center gap-2"
                          data-testid={`edit-permissions-${role.id}`}
                        >
                          <Settings className="w-4 h-4" />
                          {isSystemRole ? 'Voir' : 'Permissions'}
                        </button>
                      )}
                      {!isSystemRole && can('users.edit') && (
                        <button
                          onClick={() => openEditRoleModal(role)}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Modifier le rôle"
                          data-testid={`edit-role-${role.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {!isSystemRole && can('users.delete') && (
                        <button
                          onClick={() => handleDeleteRole(role.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer le rôle"
                          data-testid={`delete-role-${role.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Permission badges */}
                  {!hasAllAccess && permCount > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions)
                        .slice(0, 6)
                        .map((perm, i) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {PERMISSION_LABELS[perm.split('.')[0]] || perm.split('.')[0]} - {perm.split('.')[1]}
                          </span>
                        ))}
                      {permCount > 6 && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                          +{permCount - 6} autres
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Permissions Modal - Must be outside of tab conditionals */}
      {showPermissionsModal && editingRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-testid="permissions-modal">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                Permissions - {editingRole.name}
              </h2>
              <button onClick={() => setShowPermissionsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {(editingRole.is_system || editingRole.code === 'owner' || editingRole.code === 'super_admin') ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-blue-500" />
                  <p className="font-medium text-gray-900">Rôle système</p>
                  <p className="mt-2">Le rôle <strong>{editingRole.name}</strong> a accès complet à toutes les fonctionnalités.</p>
                  <p className="text-sm mt-2 text-amber-600">Les permissions des rôles système ne peuvent pas être modifiées.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(availablePermissions).map(([module, permissions]) => (
                    <div key={module} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleModule(module)}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <span className="font-medium">{PERMISSION_LABELS[module] || module}</span>
                        {expandedModules[module] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      {expandedModules[module] && (
                        <div className="p-3 space-y-2">
                          {permissions.map((perm) => (
                            <label
                              key={perm.value}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={editedPermissions.includes(perm.value)}
                                onChange={() => togglePermission(perm.value)}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <div>
                                <p className="text-sm font-medium">{perm.label}</p>
                                <p className="text-xs text-muted-foreground">{perm.value}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!(editingRole.is_system || editingRole.code === 'owner' || editingRole.code === 'super_admin') && (
              <div className="flex justify-end gap-3 p-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowPermissionsModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button onClick={savePermissions} className="btn-primary" data-testid="save-permissions">
                  Enregistrer
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}
              </h2>
              <button onClick={() => setShowRoleModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRoleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du rôle *
                  </label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    className="input-field"
                    placeholder="Ex: Réceptionniste"
                    required
                    data-testid="role-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code *
                  </label>
                  <input
                    type="text"
                    value={roleForm.code}
                    onChange={(e) => setRoleForm({ ...roleForm, code: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                    className="input-field"
                    placeholder="Ex: RECEPTIONNISTE"
                    required
                    data-testid="role-code-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="input-field"
                  placeholder="Description du rôle..."
                  data-testid="role-description-input"
                />
              </div>
              
              {/* Permissions Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions
                </label>
                <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                  {Object.entries(availablePermissions).map(([module, permissions]) => (
                    <div key={module} className="border-b last:border-b-0">
                      <div className="p-3 bg-gray-50 font-medium text-sm">
                        {PERMISSION_LABELS[module] || module}
                      </div>
                      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {permissions.map((perm) => (
                          <label
                            key={perm.value}
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={roleForm.permissions?.includes(perm.value)}
                              onChange={() => toggleRolePermission(perm.value)}
                              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <span className="text-sm">{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowRoleModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" data-testid="save-role-btn">
                  {editingRole ? 'Mettre à jour' : 'Créer le rôle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
